/**
 * Calculate the minimum and maximum year constraints for date of birth input.
 * @returns Object with minYear and maxYear
 */
export function getYearConstraints() {
  const now = new Date();
  const minYear = now.getFullYear() - 100; // 100 years ago (maximum age)
  const maxYear = now.getFullYear() - 17; // 17 years ago (minimum age)
  return { minYear, maxYear };
}

/**
 * Converts a 2-digit year to a 4-digit year based on current date context.
 * Years that would be in the future become 20xx, past years become 19xx.
 *
 * @param twoDigitYear - The 2-digit year (e.g., "30", "90")
 * @returns The 4-digit year as a string (e.g., "2030", "1990")
 */
export function convertTwoDigitYear(twoDigitYear: string): string {
  const currentYear = new Date().getFullYear();
  const currentYearLastTwoDigits = currentYear % 100;
  const yearValue = parseInt(twoDigitYear, 10);

  // If the 2-digit year is greater than current year's last 2 digits,
  // it's likely a past century year (19xx), otherwise it's a current century year (20xx)
  if (yearValue > currentYearLastTwoDigits) {
    return `19${twoDigitYear.padStart(2, "0")}`;
  } else {
    return `20${twoDigitYear.padStart(2, "0")}`;
  }
}

/**
 * Converts a 3-digit year to a 4-digit year based on current date context.
 * Years that would be in the future become 20xx, past years become 19xx.
 *
 * @param threeDigitYear - The 3-digit year (e.g., "030", "900")
 * @returns The 4-digit year as a string (e.g., "2030", "1900")
 */
export function convertThreeDigitYear(threeDigitYear: string): string {
  // Take only the last 3 digits and ensure it's 3 digits
  const lastThree = (parseInt(threeDigitYear, 10) % 1000).toString().padStart(3, "0");
  const year2xxx = 2000 + parseInt(lastThree, 10);

  return year2xxx <= new Date().getFullYear() ? `2${lastThree}` : `1${lastThree}`;
}

/**
 * Converts a single digit year to a 4-digit year based on current date context.
 * Single digit years are treated as recent years (20xx).
 *
 * @param singleDigitYear - The single digit year (e.g., "5", "9")
 * @returns The 4-digit year as a string (e.g., "2005", "2009")
 */
export function convertSingleDigitYear(singleDigitYear: string): string {
  return `200${singleDigitYear}`;
}

/**
 * Converts a single digit month to a 2-digit month with leading zero.
 *
 * @param singleDigitMonth - The single digit month (e.g., "5", "9")
 * @returns The 2-digit month as a string (e.g., "05", "09")
 */
export function convertSingleDigitMonth(singleDigitMonth: string): string {
  return singleDigitMonth.padStart(2, "0");
}

/**
 * Converts a single digit day to a 2-digit day with leading zero.
 *
 * @param singleDigitDay - The single digit day (e.g., "5", "9")
 * @returns The 2-digit day as a string (e.g., "05", "09")
 */
export function convertSingleDigitDay(singleDigitDay: string): string {
  return singleDigitDay.padStart(2, "0");
}
